import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ThemeContext, type ThemePreference } from './context';
import { SETTINGS_KEYS, readSetting, writeSetting } from '../lib/storage';

const PREFERENCES: readonly ThemePreference[] = ['system', 'light', 'dark'];

/** Kept in sync with --bg in tokens.css. */
const BAR_COLOUR = { light: '#f6f5f3', dark: '#0d0d0f' } as const;

function systemPrefersDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/**
 * The browser chrome colour is declared twice in index.html, once per scheme.
 * When the user overrides the system, both entries are pinned to the chosen
 * colour so whichever one the browser matches is the right one.
 */
function paintBrowserChrome(preference: ThemePreference, resolved: 'light' | 'dark') {
  for (const scheme of ['light', 'dark'] as const) {
    const meta = document.querySelector<HTMLMetaElement>(
      `meta[name="theme-color"][media*="${scheme}"]`,
    );
    if (meta) meta.content = preference === 'system' ? BAR_COLOUR[scheme] : BAR_COLOUR[resolved];
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(() =>
    readSetting(SETTINGS_KEYS.theme, PREFERENCES, 'system'),
  );
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent) => setSystemDark(event.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const resolved: 'light' | 'dark' =
    preference === 'system' ? (systemDark ? 'dark' : 'light') : preference;

  useEffect(() => {
    const root = document.documentElement;
    if (preference === 'system') root.removeAttribute('data-theme');
    else root.dataset.theme = preference;
    paintBrowserChrome(preference, resolved);
  }, [preference, resolved]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    writeSetting(SETTINGS_KEYS.theme, next);
  }, []);

  const value = useMemo(
    () => ({ preference, resolved, setPreference }),
    [preference, resolved, setPreference],
  );

  return <ThemeContext value={value}>{children}</ThemeContext>;
}
